import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../contextStore/AuthContext';
import { ToastContext } from '../../../contextStore/ToastContext';
import { usersRef } from '../../../firebase/collections';
import { silentCatch } from '../../../utils/errorHandler';
import { useSettingsData } from './useSettingsData';
import { handleExportData, saveNotifPrefs, handleChangePassword, handleDeleteAccount } from './settingsHandlers';
import { ExportDataSection, AddressesSection, NotifPrefsSection } from './SettingsSections';
import '../Settings.css';

export default function Settings() {
  const { t } = useTranslation();
  const { user, setUser } = useContext(AuthContext);
  const history = useHistory();
  const { addToast } = useContext(ToastContext);

  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const { notifPrefs, setNotifPrefs, notifPrefsLoading, setNotifPrefsLoading, addresses, addressesLoading } = useSettingsData(user);

  // Load newsletter subscription state from user doc
  useEffect(() => {
    if (!user?.uid) return;
    usersRef()
      .doc(user.uid)
      .get()
      .then((doc) => {
        if (doc.exists) {
          setNewsletterSubscribed(Boolean(doc.data().emailSubscribed));
        }
      })
      .catch(silentCatch('Settings:loadNewsletter'));
  }, [user?.uid]);

  const onToggleNewsletter = (checked) => {
    setNewsletterLoading(true);
    setNewsletterSubscribed(checked);
    usersRef()
      .doc(user.uid)
      .set({ emailSubscribed: checked }, { merge: true })
      .then(() => {
        addToast(
          checked ? t('settings.newsletterEnabled') : t('settings.newsletterDisabled'),
          'success'
        );
      })
      .catch(() => {
        setNewsletterSubscribed(!checked);
        addToast(t('settings.newsletterToggleFailed'), 'error');
      })
      .finally(() => setNewsletterLoading(false));
  };

  const isEmailProvider = user?.providerData?.[0]?.providerId === 'password';
  const isAnonymous = user?.isAnonymous;

  const onExport = () => handleExportData(user, addToast);
  const onSaveNotifPrefs = (next) => saveNotifPrefs(user, next, setNotifPrefs, setNotifPrefsLoading, addToast);
  const onSubmitPassword = (e) => handleChangePassword(e, user, passwordForm, setPasswordForm, setPasswordLoading, setPasswordError, addToast);
  const onSubmitDelete = (e) =>
    handleDeleteAccount(e, user, setUser, isEmailProvider, isAnonymous, deleteConfirm, deletePassword, setDeleteLoading, addToast, history);

  return (
    <div className="dashboardSettings">
      <h2 className="dashboardSettingsTitle">{t('settings.accountSettings')}</h2>
      <ExportDataSection onExport={onExport} />
      <AddressesSection addresses={addresses} loading={addressesLoading} />
      <NotifPrefsSection
        notifPrefs={notifPrefs}
        loading={notifPrefsLoading || newsletterLoading}
        onToggle={onSaveNotifPrefs}
        newsletterSubscribed={newsletterSubscribed}
        onToggleNewsletter={onToggleNewsletter}
      />

      {isEmailProvider && (
        <section className="dashboardSettingsSection">
          <h3>{t('settings.changePassword')}</h3>
          <form onSubmit={onSubmitPassword}>
            <label htmlFor="current-pw">{t('settings.currentPassword')}</label>
            <input id="current-pw" type="password" value={passwordForm.current} onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))} required />
            <label htmlFor="new-pw">{t('settings.newPassword')}</label>
            <input id="new-pw" type="password" value={passwordForm.new} onChange={(e) => setPasswordForm((p) => ({ ...p, new: e.target.value }))} required minLength={6} />
            <label htmlFor="confirm-pw">{t('settings.confirmNewPassword')}</label>
            <input id="confirm-pw" type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm((p) => ({ ...p, confirm: e.target.value }))} required />
            {passwordError && <p className="settingsError">{passwordError}</p>}
            <button type="submit" disabled={passwordLoading}>{passwordLoading ? t('settings.updating') : t('settings.updatePassword')}</button>
          </form>
        </section>
      )}

      <section className="dashboardSettingsSection">
        <h3>{t('settings.dangerZone')}</h3>
        <p className="settingsHint">{t('settings.deleteWarning')}</p>
        <form onSubmit={onSubmitDelete}>
          <label htmlFor="delete-confirm">{t('settings.typeDelete')}</label>
          <input id="delete-confirm" type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" />
          {isEmailProvider && (
            <>
              <label htmlFor="delete-password">{t('settings.currentPassword')}</label>
              <input id="delete-password" type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t('settings.yourPassword')} />
            </>
          )}
          <button type="submit" className="settingsDeleteBtn" disabled={deleteLoading || deleteConfirm !== 'DELETE' || (isEmailProvider && !deletePassword)}>
            {deleteLoading ? t('settings.deleting') : t('settings.deleteAccount')}
          </button>
        </form>
      </section>
    </div>
  );
}
